import { useParams, Link } from 'react-router-dom'
import Seo from '../components/Seo.jsx'
import Related from '../components/Related.jsx'
import { getArticle, formatDate } from '../lib/articles.js'
import { withBrand } from '../lib/seoTitle.js'

/*
 * Single article (/articles/:slug). Only LIVE articles are prerendered (see
 * App.jsx getStaticPaths + src/lib/articles.js), so a null lookup here means a
 * draft/scheduled or unknown slug — we show a small fallback rather than crash.
 */
export default function Article() {
  const { slug } = useParams()
  const a = getArticle(slug)

  if (!a) {
    return (
      <main className="page">
        <Seo title="Article not found | CalculateMyPeptides" noindex />
        <div className="container page-narrow" style={{ textAlign: 'center', padding: '40px 0' }}>
          <h1 className="section-title">Article not found</h1>
          <p className="section-sub">This article may have moved or isn’t published yet.</p>
          <Link to="/articles" className="btn btn-primary">← All articles</Link>
        </div>
      </main>
    )
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: a.title,
    description: a.description,
    datePublished: a.date,
    dateModified: a.date,
    image: `https://calculatemypeptides.com${a.cover}`,
    author: { '@type': 'Organization', name: a.author, url: 'https://calculatemypeptides.com/' },
    publisher: {
      '@type': 'Organization',
      name: 'CalculateMyPeptides',
      logo: { '@type': 'ImageObject', url: 'https://calculatemypeptides.com/favicon.svg' },
    },
    mainEntityOfPage: `https://calculatemypeptides.com/articles/${a.slug}`,
  }

  return (
    <main className="page">
      <Seo
        title={withBrand(a.title)}
        description={a.description}
        image={a.cover}
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Articles', path: '/articles' },
          { name: a.title, path: `/articles/${a.slug}` },
        ]}
      />
      <div className="container page-narrow">
        <Link to="/articles" className="pep-back">← All articles</Link>

        <article className="article">
          <header className="article-head">
            <h1 className="article-title">{a.title}</h1>
            <div className="article-meta">
              <span>{formatDate(a.date)}</span>
              <span aria-hidden>·</span>
              <span>{a.readingMins} min read</span>
              {a.tags.length > 0 && <span aria-hidden>·</span>}
              {a.tags.map((t) => <span key={t} className="pep-tag">{t}</span>)}
            </div>
          </header>

          {/* Body is our own Markdown, converted to HTML at build time. */}
          <div className="prose" dangerouslySetInnerHTML={{ __html: a.html }} />

          <p className="article-disclaimer">
            This article is for educational purposes only and is not medical advice. Peptides and GLP-1 medications
            should be used under the guidance of a qualified healthcare provider. Always follow the directions of your
            prescriber and product labeling.
          </p>

          <div className="article-cta card">
            <div>
              <strong>Working out your own dose?</strong>
              <span className="muted"> Get the exact units to draw in seconds.</span>
            </div>
            <Link to="/calculator" className="btn btn-primary">Open the calculator →</Link>
          </div>
        </article>

        <Related
          title="Keep reading"
          links={[
            { to: '/articles', label: 'All articles', desc: 'More explainers' },
            { to: '/calculator', label: 'Peptide calculator', desc: 'Run your numbers' },
            { to: '/peptides', label: 'Peptide library', desc: 'Per-compound profiles' },
            { to: '/guides', label: 'Guides', desc: 'Reconstitution & dosing' },
          ]}
        />
      </div>
    </main>
  )
}
